import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { aboutInfo, faqData, siteInfo } from '@/mocks/site';

export default function About() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'AboutPage',
        name: '关于果冻传媒',
        description: siteInfo.description,
        url: 'https://www.guodongmedia.com/about',
      },
      {
        '@type': 'Organization',
        name: '果冻传媒',
        url: 'https://www.guodongmedia.com/',
        description: siteInfo.description,
        email: siteInfo.email,
        telephone: siteInfo.phone,
        address: {
          '@type': 'PostalAddress',
          streetAddress: '传媒文化产业园A座',
          addressLocality: '北京',
          addressRegion: '朝阳区',
          addressCountry: 'CN',
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.guodongmedia.com/' },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqData.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <Navbar />

      <main className="pt-24 pb-16">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          {/* Page Header */}
          <div className="text-center mb-12 md:mb-16">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
              关于果冻传媒
            </h1>
            <p className="text-foreground-500 max-w-[600px] mx-auto text-sm md:text-base">
              专注优质视频内容，服务全球华语用户
            </p>
          </div>

          {/* Brand Introduction */}
          <section className="mb-16 md:mb-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-6">
                  {aboutInfo.brandIntro.title}
                </h2>
                {aboutInfo.brandIntro.paragraphs.map((p, idx) => (
                  <p key={idx} className="text-foreground-700 leading-relaxed mb-4 text-sm md:text-base">
                    {p}
                  </p>
                ))}
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src={aboutInfo.brandIntro.image}
                  alt="果冻传媒品牌介绍"
                  className="w-full h-auto object-cover rounded-2xl"
                />
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="mb-16 md:mb-20">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 text-center mb-10">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200 -translate-x-1/2 hidden md:block"></div>
              <div className="flex flex-col gap-6">
                {aboutInfo.milestones.map((milestone, idx) => (
                  <div
                    key={milestone.year}
                    className={`flex flex-col md:flex-row gap-4 md:gap-8 items-start ${
                      idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                      <div className="bg-background-100 rounded-2xl p-5 md:p-6">
                        <span className="inline-block px-4 py-1.5 bg-primary-500 text-background-50 rounded-full text-sm font-bold mb-3">
                          {milestone.year}
                        </span>
                        <h3 className="text-lg font-heading font-bold text-foreground-900 mb-2">
                          {milestone.title}
                        </h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          {milestone.description}
                        </p>
                      </div>
                    </div>
                    <div className="hidden md:flex w-8 items-center justify-center flex-shrink-0">
                      <div className="w-3 h-3 rounded-full bg-primary-500 ring-4 ring-background-50 z-10"></div>
                    </div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="mb-16 md:mb-20">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 text-center mb-10">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {aboutInfo.testimonials.map((t, idx) => (
                <div key={idx} className="bg-background-100 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <div className="font-medium text-foreground-900 text-sm">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    "{t.content}"
                  </p>
                  <div className="flex items-center gap-1 mt-3">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="mb-16 md:mb-20">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 text-center mb-10">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: 'ri-mail-line', label: '电子邮箱', value: siteInfo.email },
                { icon: 'ri-phone-line', label: '客服电话', value: siteInfo.phone },
                { icon: 'ri-map-pin-line', label: '公司地址', value: siteInfo.address },
              ].map((contact) => (
                <div key={contact.label} className="bg-background-100 rounded-2xl p-6 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-primary-100">
                    <i className={`${contact.icon} text-2xl text-primary-600`}></i>
                  </div>
                  <h3 className="font-medium text-foreground-900 mb-1 text-sm">{contact.label}</h3>
                  <p className="text-sm text-foreground-600">{contact.value}</p>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section>
            <h2 className="text-2xl font-heading font-bold text-foreground-950 text-center mb-10">
              常见问题
            </h2>
            <div className="max-w-[700px] mx-auto flex flex-col gap-3">
              {faqData.map((faq, idx) => (
                <details key={idx} className="group bg-background-100 rounded-xl p-5 cursor-pointer">
                  <summary className="flex items-center justify-between font-medium text-foreground-900 text-sm md:text-base list-none">
                    {faq.question}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                  </summary>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </details>
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}